import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useState } from 'react';
import { BUILD_BETA } from '../../constants/build-quality';
import { isLicExpired, isLicExpiringSoon, isLicIncompatibleVersion, isLicInvalid, isLicWarningRequired } from '../../constants/license-constants';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import betaIcon from '../../images/Beta.svg';
import brandLogo from '../../images/brand_logo_white.png';
import productLogo from '../../images/product_logo_menu.svg';
import MenuBurgerIcon from '../../images/TOC.svg';
import { useGlobalState } from '../../Providers';
import AboutModal from '../about/AboutModal';
import CopyrightModal from '../about/CopyrightModal';
import LicenseModal from '../about/LicenseModal';
import Menu from '../menu/menu';
import Tooltip from '../Tooltip/Tooltip';

type Props = {
    onMenuSelect: any,
    menuItems: any[],
    isCompact: boolean,
    setIsCompact: (boolean) => void,
    currentPage?: string
}

const SideMenu: FC<Props> = ({ onMenuSelect, menuItems, isCompact, setIsCompact, currentPage }) => {
    const [openAbout, setOpenAbout] = useState<boolean>(false);
    const [openCopyright, setOpenCopyright] = useState<boolean>(false);
    const [openLicense, setOpenLicense] = useState<boolean>(false);
    const { buildInfo } = useGlobalState() ?? {};
    const { translate } = useLocale();

    const aboutModalButtonClick = (buttonClicked: string) => {
        switch (buttonClicked) {
            case "copyright":
                setOpenAbout(false);
                setOpenCopyright(true);
                break;
            case "license":
                setOpenAbout(false);
                setOpenLicense(true);
                break;
        }
    }

    const getTooltipText = () => {
        let text = '';

        if (isLicExpired(buildInfo?.licenseStatus)) {
            text = translate.byKeyFormatted(TranslationKeys.licenseExpiredShort, [buildInfo?.licenseName, buildInfo?.licenseDate]);
        } else if (isLicExpiringSoon(buildInfo?.licenseStatus)) {
            text = translate.byKeyFormatted(TranslationKeys.licenseExpiringSoonShort, [buildInfo?.licenseDate]);
        } else if (isLicIncompatibleVersion(buildInfo?.licenseStatus)) {
            text = translate.byKeyFormatted(TranslationKeys.licenseIncompatibleVersionShort, [buildInfo?.licenseName]);
        }

        return text;
    }

    const getVersion = () => {
        const versionNumber = buildInfo?.version?.includes("9999") ? `9.9.9` : buildInfo?.version;
        return `v${versionNumber}${buildInfo?.isRunningWithReversedMifare ? '-R' : ''}`;
    }

    return (
        <div className={`h-screen overflow-hidden bg-sidemenu-primary ${isCompact ? 'w-24' : 'w-64'}`}>
            {isCompact
                ? (
                    <ul>
                        <li className={`py-6 bg-sidemenu-header flex flex-row justify-center items-center text-white ${isCompact ? '-ml-1' : ''}`}>
                            <button onClick={() => setIsCompact(!isCompact)}>
                                <img src={MenuBurgerIcon} alt="menu toggle" />
                            </button>
                        </li>
                        <li className="py-2 bg-portal-blue-secondary flex flex-row justify-center items-center text-white">
                            {buildInfo?.buildQuality === BUILD_BETA ? <img className="ml-0" src={betaIcon} alt="" /> : <span>{getVersion()}</span>}
                        </li>
                    </ul>
                )
                : (
                    <ul>
                        <li className="py-2 px-4 bg-sidemenu-header flex flex-row items-center text-white">
                            <button onClick={() => setIsCompact(!isCompact)}>
                                <img src={MenuBurgerIcon} alt="menu toggle" />
                            </button>
                            <img src={brandLogo} alt="branding" style={{ height: '3.8rem' }} />
                        </li>
                        <li className="py-2 px-4 bg-portal-blue-secondary flex flex-column text-xs text-white font-semibold">
                            <div style={{ width: '100%' }} className="cursor-pointer" onClick={() => setOpenAbout(true)}>
                                <span className="menu-versioning w-full">
                                    <span className="flex flex-row justify-between items-baseline">
                                        {buildInfo ?.licenseStatus !== undefined && isLicWarningRequired(buildInfo?.licenseStatus)
                                            ? 
                                            <Tooltip text={getTooltipText()} direction='bottom' show={true}>
                                                <div>
                                                
                                                    {isLicInvalid(buildInfo?.licenseStatus)
                                                        ? <FontAwesomeIcon style={{color: 'red'}} icon={faTriangleExclamation} />
                                                        :  <FontAwesomeIcon style={{color: 'yellow'}} icon={faTriangleExclamation} />
                                                    }

                                                    <span
                                                        className="pl-1 ml-px">
                                                        {buildInfo?.licenseShortHand}
                                                    </span>
                                                </div>
                                            </Tooltip>
                                            : <span className="pl-px ml-px">{buildInfo?.licenseShortHand}</span>}

                                        <span className="flex flex-row">
                                            <span>{getVersion()}</span>
                                            {buildInfo?.buildQuality === BUILD_BETA ? <img className="ml-4" src={betaIcon} alt="" /> : null}
                                        </span>
                                    </span>
                                </span>
                                <img src={productLogo} alt="" className="w-48 h-6 menu-product-logo" />
                            </div>
                        </li>
                    </ul>
                )
            }
            <Menu
                className="mt-4"
                menu={menuItems}
                onClick={onMenuSelect}
                compact={isCompact}
                currentPage={currentPage} />
            <AboutModal isOpen={openAbout} onClose={() => setOpenAbout(false)} aboutModalButtonClick={aboutModalButtonClick} />
            <CopyrightModal isOpen={openCopyright} onClose={() => setOpenCopyright(false)} />
            <LicenseModal isOpen={openLicense} onClose={() => setOpenLicense(false)} />
        </div>
    )
}

export default SideMenu;
